import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { userAPI, evaluationAPI, projectAPI, departmentAPI, featureAPI, storyPointAPI } from '../../api/services';
import NotificationPanel from '../Common/NotificationPanel';


const ProjectManagement = React.lazy(() => import('./ProjectManagement'));
const ProjectBoard = React.lazy(() => import('../Common/ProjectBoard'));
const AdminFeatureModal = React.lazy(() => import('./AdminFeatureModal'));
const AdminProjectModal = React.lazy(() => import('./AdminProjectModal'));

type DashTab = 'overview' | 'users' | 'board' | 'evaluations' | 'all-projects' | 'departments';

const TABS: { key: DashTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'all-projects', label: 'All Projects' },
  { key: 'users', label: 'Users' },
  { key: 'board', label: 'Board' },
  { key: 'departments', label: 'Departments' },
  { key: 'evaluations', label: 'Evaluations' },
];

const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '';
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
const timeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDate(d);
};

const STATUS_DOT: Record<string, string> = {
  development: 'bg-blue-500', testing: 'bg-yellow-500', production: 'bg-gray-500',
  planning: 'bg-gray-400', uat: 'bg-orange-400', completed: 'bg-gray-500',
};

const AdminDashboardNew: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppSelector(s => s.auth);
  const orgRole = (user as any)?.organizationRole;
  const priority = orgRole?.priority || 0;
  const isFullAdmin = priority >= 90;
  const userName = user?.name?.split(' ')[0] || 'Admin';

  const tabParam = searchParams.get('tab') as DashTab | null;
  const [activeTab, setActiveTab] = useState<DashTab>(tabParam && TABS.some(t => t.key === tabParam) ? tabParam : 'overview');

  // Data
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [pendingFeatures, setPendingFeatures] = useState<any[]>([]);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [evalPeriods, setEvalPeriods] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [deptMembers, setDeptMembers] = useState<any[]>([]);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const handleTabChange = (tab: DashTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        userAPI.getAllUsers(),
        projectAPI.getAllProjects(),
        isFullAdmin
          ? departmentAPI.getAllDepartments({ includeMembers: true })
          : departmentAPI.getMyManagedDepartments(),
        featureAPI.getPendingApprovals(),
        evaluationAPI.getAllEvaluationPeriods(),
        storyPointAPI.getDepartmentBoard(),
      ]);

      if (results[0].status === 'fulfilled') setUsers(results[0].value.data?.data || results[0].value.data || []);
      if (results[1].status === 'fulfilled') setProjects(results[1].value.data?.data || results[1].value.data || []);
      if (results[2].status === 'fulfilled') setDepartments(results[2].value.data?.data || results[2].value.data || []);
      if (results[3].status === 'fulfilled') setPendingFeatures(results[3].value.data?.data || results[3].value.data || []);
      if (results[4].status === 'fulfilled') {
        const { periods, defaultPeriod } = results[4].value.data;
        setEvalPeriods(periods || []);
        setSelectedPeriod(defaultPeriod || '');
        // Fetch evaluations for default period
        if (defaultPeriod) {
          const evRes = await evaluationAPI.getAllEvaluations({ evaluationPeriod: defaultPeriod });
          setEvaluations(evRes.data || []);
        }
      }
      if (results[5].status === 'fulfilled') setDeptMembers(results[5].value.data?.data?.members || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [isFullAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch evaluations when period changes
  const handlePeriodChange = async (period: string) => {
    setSelectedPeriod(period);
    try {
      const res = await evaluationAPI.getAllEvaluations({ evaluationPeriod: period });
      setEvaluations(res.data || []);
    } catch { /* ignore */ }
  };

  // Computed stats
  const activeProjects = useMemo(() => projects.filter(p => p.status === 'development' || p.status === 'testing'), [projects]);
  const submittedEvals = useMemo(() => evaluations.filter(e => e.status === 'submitted'), [evaluations]);
  const draftEvals = useMemo(() => evaluations.filter(e => e.status === 'draft'), [evaluations]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Welcome back, {userName}</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          {isFullAdmin ? 'Organization overview' : `${departments.length > 0 ? departments.map((d: any) => d.name).join(', ') : 'Department'} overview`}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div>
              {/* Pending approvals */}
              {pendingFeatures.length > 0 && (
                <div>
                  <div className="px-6 py-2 border-b border-gray-100">
                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Pending Feature Approvals</span>
                  </div>
                  {pendingFeatures.slice(0, 8).map((f: any) => (
                    <div key={f._id} onClick={() => setSelectedFeatureId(f._id)}
                      className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
                      <div className="col-span-5 flex items-center gap-2.5 min-w-0">
                        <svg className="w-3.5 h-3.5 text-yellow-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span className="text-sm text-gray-700 truncate">{f.name}</span>
                      </div>
                      <div className="col-span-2 flex items-center text-xs text-gray-500 truncate">{f.projectName || ''}</div>
                      <div className="col-span-2 flex items-center text-xs text-gray-400">{f.owner?.name || ''}</div>
                      <div className="col-span-1 flex items-center text-xs text-blue-600 tabular-nums">{f.storyPoints || 0} SP</div>
                      <div className="col-span-2 flex items-center justify-end">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">Pending</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Active projects */}
              <div>
                <div className="px-6 py-2 border-b border-gray-100">
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Active Projects</span>
                </div>
                {activeProjects.length === 0 ? (
                  <p className="text-center py-8 text-xs text-gray-400">No active projects</p>
                ) : activeProjects.slice(0, 10).map((p: any) => (
                  <div key={p._id} onClick={() => setSelectedProjectId(p._id)}
                    className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
                    <div className="col-span-4 flex items-center gap-2.5 min-w-0">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[p.status] || 'bg-gray-400'}`} />
                      <span className="text-sm text-gray-700 truncate">{p.name}</span>
                    </div>
                    <div className="col-span-2 flex items-center text-xs text-gray-500">{cap(p.status || '')}</div>
                    <div className="col-span-2 flex items-center text-xs text-gray-400 truncate">{p.owner?.name || ''}</div>
                    <div className="col-span-2 flex items-center text-xs text-gray-400">{p.members?.length || 0} members</div>
                    <div className="col-span-2 flex items-center justify-end text-xs text-gray-400">{fmtDate(p.updatedAt)}</div>
                  </div>
                ))}
              </div>

            </div>
          )}

          {/* ── Users tab ── */}
          {activeTab === 'users' && (
            <UsersTab users={users} onRefresh={fetchData} />
          )}

          {/* ── Evaluations tab ── */}
          {activeTab === 'evaluations' && (
            <EvaluationsTab
              evaluations={evaluations}
              periods={evalPeriods}
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
              onRefresh={fetchData}
            />
          )}

          {/* ── All Projects tab ── */}
          {activeTab === 'all-projects' && (
            <React.Suspense fallback={
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            }>
              <ProjectManagement />
            </React.Suspense>
          )}

          {activeTab === 'board' && (
            <React.Suspense fallback={
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
              </div>
            }>
              <ProjectBoard />
            </React.Suspense>
          )}

          {/* ── Departments tab ── */}
          {activeTab === 'departments' && (
            <DepartmentsTab departments={departments} members={deptMembers} onRefresh={fetchData} />
          )}
        </div>

        {/* Right sidebar — quick actions + recent */}
        {activeTab === 'overview' && (
          <div className="w-72 border-l border-gray-100 shrink-0">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Quick actions</h3>
              <div className="mt-2 space-y-1">
                <button onClick={() => navigate('/admin/people?tab=users')} className="w-full text-left text-xs text-gray-600 hover:text-blue-600 py-1.5 transition-colors">Manage users</button>
                <button onClick={() => navigate('/admin/management?tab=governance')} className="w-full text-left text-xs text-gray-600 hover:text-blue-600 py-1.5 transition-colors">Review approvals</button>
                <button onClick={() => navigate('/admin/management?tab=reviews')} className="w-full text-left text-xs text-gray-600 hover:text-blue-600 py-1.5 transition-colors">Assign reviews</button>
                <button onClick={() => navigate('/admin/config')} className="w-full text-left text-xs text-gray-600 hover:text-blue-600 py-1.5 transition-colors">Configuration</button>
              </div>
            </div>

            {/* Notifications */}
            <div className="px-4 py-3 border-b border-gray-100">
              <NotificationPanel limit={5} title="Notifications" />
            </div>

            {/* Department summary */}
            {deptMembers.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Team SP this month</h3>
                <div className="mt-2 space-y-2">
                  {deptMembers.filter((m: any) => (m.currentMonthNet || 0) > 0).slice(0, 6).map((m: any) => (
                    <div key={m.userId} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[8px] flex items-center justify-center shrink-0">{(m.name || '?')[0].toUpperCase()}</div>
                        <span className="text-xs text-gray-600 truncate capitalize">{m.name}</span>
                      </div>
                      <span className="text-xs text-blue-600 font-medium tabular-nums shrink-0">{m.currentMonthNet || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evaluations summary */}
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Evaluations{selectedPeriod ? ` — ${selectedPeriod}` : ''}</h3>
              <div className="mt-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Submitted</span>
                  <span className="text-xs text-gray-700 tabular-nums">{submittedEvals.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Drafts</span>
                  <span className="text-xs text-gray-700 tabular-nums">{draftEvals.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Not started</span>
                  <span className="text-xs text-gray-700 tabular-nums">{Math.max(0, users.length - evaluations.length)}</span>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="px-4 py-3">
              <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">At a glance</h3>
              <div className="mt-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Users</span>
                  <span className="text-xs text-gray-700 tabular-nums">{users.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Projects</span>
                  <span className="text-xs text-gray-700 tabular-nums">{projects.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Active</span>
                  <span className="text-xs text-blue-600 tabular-nums">{activeProjects.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Pending approvals</span>
                  <span className="text-xs text-gray-700 tabular-nums">{pendingFeatures.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Departments</span>
                  <span className="text-xs text-gray-700 tabular-nums">{departments.length}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <React.Suspense fallback={null}>
        {selectedFeatureId && (
          <AdminFeatureModal
            featureId={selectedFeatureId}
            onClose={() => setSelectedFeatureId(null)}
            onRefresh={fetchData}
          />
        )}
        {selectedProjectId && (
          <AdminProjectModal
            projectId={selectedProjectId}
            onClose={() => setSelectedProjectId(null)}
            onRefresh={fetchData}
          />
        )}
      </React.Suspense>
    </div>
  );
};

// ─── Users sub-tab ───────────────────────────────────────────────────────────
const AdminUserModal = React.lazy(() => import('./AdminUserModal'));

const UsersTab: React.FC<{ users: any[]; onRefresh: () => void }> = ({ users, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const perPage = 15;

  const filtered = useMemo(() => users.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  ), [users, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [search]);

  return (
    <div>
      <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="w-64">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..."
            className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-full outline-none focus:ring-1 focus:ring-gray-300" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{filtered.length} users</span>
          <button onClick={() => setShowCreate(true)}
            className="text-xs text-gray-400 hover:text-blue-600 transition-colors">
            + Add User
          </button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
        <div className="col-span-4">Name</div>
        <div className="col-span-3">Email</div>
        <div className="col-span-1">Level</div>
        <div className="col-span-2">Role</div>
        <div className="col-span-2 text-right">Status</div>
      </div>
      {paginated.map((u: any) => (
        <div key={u._id} onClick={() => setSelectedUserId(u._id)}
          className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
          <div className="col-span-4 flex items-center gap-2.5 min-w-0">
            <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center shrink-0">{(u.name || '?')[0].toUpperCase()}</div>
            <span className="text-sm text-gray-700 truncate capitalize">{u.name}</span>
          </div>
          <div className="col-span-3 flex items-center text-xs text-gray-500 truncate">{u.email}</div>
          <div className="col-span-1 flex items-center text-xs text-gray-500">{u.level || '—'}</div>
          <div className="col-span-2 flex items-center text-xs text-gray-400">{cap(u.role || '')}</div>
          <div className="col-span-2 flex items-center justify-end">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.isActive !== false ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}`}>
              {u.isActive !== false ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      ))}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3">
          <span className="text-xs text-gray-400">{(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Prev</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {/* User modal */}
      <React.Suspense fallback={null}>
        {(selectedUserId || showCreate) && (
          <AdminUserModal
            userId={selectedUserId}
            onClose={() => { setSelectedUserId(null); setShowCreate(false); }}
            onRefresh={onRefresh}
          />
        )}
      </React.Suspense>
    </div>
  );
};

// ─── Evaluations sub-tab ─────────────────────────────────────────────────────
const AdminEvalModalLazy = React.lazy(() => import('./AdminEvaluationModal'));

const EvaluationsTab: React.FC<{
  evaluations: any[]; periods: string[]; selectedPeriod: string;
  onPeriodChange: (p: string) => void; onRefresh: () => void;
}> = ({ evaluations, periods, selectedPeriod, onPeriodChange, onRefresh }) => {
  const [sp, setSp] = useSearchParams();
  const [search, setSearch] = useState('');
  const evalParam = sp.get('eval');
  const [selectedEvalId, setSelectedEvalId] = useState<string | null>(evalParam);
  const [page, setPage] = useState(1);
  const perPage = 20;

  const openEval = (id: string) => {
    setSelectedEvalId(id);
    setSp(prev => { const next = new URLSearchParams(prev); next.set('eval', id); return next; }, { replace: true });
  };
  const closeEval = () => {
    setSelectedEvalId(null);
    setSp(prev => { const next = new URLSearchParams(prev); next.delete('eval'); return next; }, { replace: true });
  };

  const filtered = useMemo(() => evaluations.filter(e => {
    const name = (e.userId as any)?.name || '';
    return !search || name.toLowerCase().includes(search.toLowerCase());
  }), [evaluations, search]);

  const submitted = filtered.filter(e => e.status === 'submitted').length;
  const drafts = filtered.filter(e => e.status === 'draft').length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [search]);

  return (
    <div>
      <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div className="w-64">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name..."
            className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-full outline-none focus:ring-1 focus:ring-gray-300" />
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{submitted} submitted</span>
          <span className="text-gray-300">·</span>
          <span>{drafts} drafts</span>
          <span className="text-gray-300">·</span>
          <span>{filtered.length} total</span>
          {periods.length > 0 && (
            <select value={selectedPeriod} onChange={e => onPeriodChange(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-gray-300">
              {periods.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
        <div className="col-span-3">Employee</div>
        <div className="col-span-2">Level</div>
        <div className="col-span-2 text-right">Emp Score</div>
        <div className="col-span-2 text-right">Admin Score</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-2 text-right">Date</div>
      </div>
      {paginated.length === 0 ? (
        <p className="text-center py-8 text-xs text-gray-400">No evaluations found</p>
      ) : paginated.map((e: any) => {
        const u = e.userId || {};
        return (
          <div key={e._id} onClick={() => openEval(e._id)}
            className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
            <div className="col-span-3 flex items-center gap-2.5 min-w-0">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center shrink-0">{(u.name || '?')[0].toUpperCase()}</div>
              <span className="text-sm text-gray-700 truncate capitalize">{u.name || 'Unknown'}</span>
            </div>
            <div className="col-span-2 flex items-center text-xs text-gray-500">{u.level || '—'}</div>
            <div className="col-span-2 flex items-center justify-end text-xs text-blue-600 tabular-nums">{e.totalScore != null ? e.totalScore.toFixed(2) : '—'}</div>
            <div className="col-span-2 flex items-center justify-end text-xs text-gray-700 tabular-nums">{e.adminTotalScore != null ? e.adminTotalScore.toFixed(2) : '—'}</div>
            <div className="col-span-1 flex items-center">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${e.status === 'submitted' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                {cap(e.status || '')}
              </span>
            </div>
            <div className="col-span-2 flex items-center justify-end text-xs text-gray-400">{fmtDate(e.updatedAt)}</div>
          </div>
        );
      })}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3">
          <span className="text-xs text-gray-400">{(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Prev</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      <React.Suspense fallback={null}>
        {selectedEvalId && (
          <AdminEvalModalLazy
            evaluationId={selectedEvalId}
            onClose={closeEval}
            onRefresh={onRefresh}
          />
        )}
      </React.Suspense>
    </div>
  );
};

// ─── Departments sub-tab ─────────────────────────────────────────────────────
const AdminDepartmentModal = React.lazy(() => import('./AdminDepartmentModal'));

const DepartmentsTab: React.FC<{ departments: any[]; members: any[]; onRefresh: () => void }> = ({ departments, members, onRefresh }) => {
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);

  return (
    <div>
      <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
        <div className="col-span-4">Department</div>
        <div className="col-span-2">Head</div>
        <div className="col-span-2 text-right">Members</div>
        <div className="col-span-2 text-right">Status</div>
        <div className="col-span-2 text-right">Created</div>
      </div>
      {departments.length === 0 ? (
        <p className="text-center py-8 text-xs text-gray-400">No departments found</p>
      ) : departments.map((d: any) => (
        <div key={d._id} onClick={() => setSelectedDeptId(d._id)}
          className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50/80 transition-colors">
          <div className="col-span-4 flex items-center gap-2.5 min-w-0">
            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" /></svg>
            <span className="text-sm text-gray-700 truncate capitalize">{d.name?.toLowerCase()}</span>
          </div>
          <div className="col-span-2 flex items-center text-xs text-gray-500 truncate">{d.headOfDepartment?.name || '—'}</div>
          <div className="col-span-2 flex items-center justify-end text-xs text-gray-500 tabular-nums">{d.memberCount ?? d.members?.length ?? 0}</div>
          <div className="col-span-2 flex items-center justify-end">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.isActive !== false ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}`}>
              {d.isActive !== false ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="col-span-2 flex items-center justify-end text-xs text-gray-400">{fmtDate(d.createdAt)}</div>
        </div>
      ))}

      <React.Suspense fallback={null}>
        {selectedDeptId && (
          <AdminDepartmentModal
            departmentId={selectedDeptId}
            onClose={() => setSelectedDeptId(null)}
            onRefresh={onRefresh}
          />
        )}
      </React.Suspense>
    </div>
  );
};

export default AdminDashboardNew;
